import React, { createRef } from 'react';
import { strings, getCurrentLocale } from '../../localization';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { ACTIONS as dispatchActions, ACTIVE_MENU_ITEM_IDS } from '../../data/layoutTemplateModularFullscreen/duck';
import { RESOURCE_GROUP_TYPES } from '../pages/dashboard/ResourceGroups/constants';
import Protected from '../../components/common/protected'

const ACTIVE_MENU_ITEM_ELEMENT_ID = "layout-main-navigation-active-item-for-main-module-group-navigation";
export class MainNavigation extends React.PureComponent {
    static propTypes = {
        features: PropTypes.object.isRequired,
        user: PropTypes.object, // used by renderWithNavigation and to check for feature flags
        ravens: PropTypes.array, // used by renderWithNavigation (edge case when on login-related views while still logged in)
        routerLocation: PropTypes.object, // adding this prop to trigger component update events during browser back/forward button clicks
        // redux:
        mainNavigationIsCollapsed: PropTypes.bool.isRequired,
        setMainNavigation: PropTypes.func.isRequired,
        mainNavigationActiveMenuItemId: PropTypes.string,
        setMainNavigationActiveMenuItemId: PropTypes.func.isRequired,
        setMainModuleGroupNavigation: PropTypes.func.isRequired,
        setMainModuleGroupSelectedResourceType: PropTypes.func.isRequired,
        setMainNavigationActiveMenuItemDomElementTop: PropTypes.func.isRequired,
        fullscreenModeIsActive: PropTypes.bool.isRequired,
        className: PropTypes.string,
    }

    constructor(props) {
        super(props);
        this.state = {
            mainNavigationHistoryIsCollapsed: false,
            mainNavigationReportsIsCollapsed: false,
            mainNavigationAccountIsCollapsed: false,
            legacyRenderWithoutNavigationModeDetected: false
        }

        this.navigationElm = createRef();
        this.resizeObserver = null; // Initialize ResizeObserver
    }

    componentDidMount() {
        const activeMenuItemId = this.getActiveMenuItemId();
        if (activeMenuItemId && !activeMenuItemId.startsWith("DASHBOARD")) { // On mount, Dashboard updates activeMenuItemId itself as per selected resource (persisted in local storage)
            this.props.setMainNavigationActiveMenuItemId(this.getActiveMenuItemId());
        }
        this.setState({
            legacyRenderWithoutNavigationModeDetected: activeMenuItemId === undefined ? true : false // set to true when active menu item id cannot be determined
        });

        this.addNavigationEventhandlers();
    }

    componentWillUnmount() {
        this.removeNavigationEventhandlers();
    }

    // Listen to changes in navigation element size to determine if bottom shadow + arrow is needed in new size
    navigationResizeHandler = (entries) => {
        const bottomShadow = this.navigationElm.current ? this.navigationElm.current.querySelector('.layout-main-navigation-bottom-shadow') : undefined;

        if (!bottomShadow) return;

        // Hide bottom shadow and arrow before checking if scrollbar is present/needed.
        // This is because these items are not actual part of the navigation content and should not be considered when checking for scrollbar presence.
        bottomShadow.style.display = 'none';

        // Check if scrollbar is present/needed
        const hasScrollbar = this.navigationElm.current.scrollHeight > this.navigationElm.current.clientHeight;

        bottomShadow.style.display = hasScrollbar ? 'flex' : 'none';

        this.navigationScrollHandler();
    }

    // Adjusts padding and margin of header and bottom shadow elements to account for vertical scrollbar width
    navigationMouseEnterHandler = (e) => {
        e.stopPropagation();

        // If the mouse enters from a child element, do not proceed
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        const navigationElm = this.navigationElm.current;

        if (!navigationElm) return;

        const header = navigationElm.querySelector('.layout-main-navigation-header > div');

        const bottomShadow = navigationElm.querySelector('.layout-main-navigation-bottom-shadow');

        const scrollbarWidth = navigationElm.offsetWidth - navigationElm.clientWidth;

        if (header) {
            const headerWidth = parseFloat(window.getComputedStyle(header).width, 10);
            header.style.width = `${headerWidth + scrollbarWidth}px`;
        }

        if (bottomShadow) {
            bottomShadow.style.paddingLeft = `${scrollbarWidth}px`;
        }

    }

    // Remove Adjustments added to padding and margin of header and bottom shadow elements to account for vertical scrollbar width
    navigationMouseLeaveHandler = (e) => {
        e.stopPropagation();

        // If the mouse leaves a child element, do not proceed
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) {
            return;
        }

        const navigationElm = this.navigationElm.current;

        if (!navigationElm) return;

        const header = navigationElm.querySelector('.layout-main-navigation-header > div');
        const bottomShadow = navigationElm.querySelector('.layout-main-navigation-bottom-shadow');

        if (header) {
            header.style.width = '';
        }

        if (bottomShadow) {
            bottomShadow.style.paddingLeft = '';
        }

    }

    // Show/hide bottom shadow and arrow based on scroll position
    navigationScrollHandler = (e) => {

        const navigationElm = this.navigationElm.current;

        if (!navigationElm) return;

        const reachedBottom = navigationElm.scrollHeight - navigationElm.scrollTop <= navigationElm.clientHeight + 3;

        const bottomShadow = navigationElm.querySelector('.layout-main-navigation-bottom-shadow');

        if (bottomShadow) {
            bottomShadow.style.visibility = reachedBottom ? 'hidden' : 'visible';
        }
    }

    addNavigationEventhandlers = () => {

        const navigationElm = this.navigationElm.current;

        if (!navigationElm) return;

        this.resizeObserver = new ResizeObserver(this.navigationResizeHandler).observe(navigationElm);
    }

    removeNavigationEventhandlers = () => {
        const navigationElm = this.navigationElm.current;

        if (!navigationElm) return;

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }

    updateMainModuleGroupNavigationAlignmentValues(activeMenuItemElement) {
        switch (this.props.mainNavigationActiveMenuItemId) {
            case ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE:
            case ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE:
            case ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET:
            case ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW:
            case ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW:
            case ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE:
            case ACTIVE_MENU_ITEM_IDS.GRID:    
                break;
            default:
                console.log("MainNavigation updateMainModuleGroupNavigationAlignmentValues ignored for " + this.props.mainNavigationActiveMenuItemId);
                return;
        }
        const domElementTop = activeMenuItemElement.getBoundingClientRect().top;
        this.props.setMainNavigationActiveMenuItemDomElementTop(domElementTop);
    }

    componentDidUpdate(prevProps) {
        // a single lookup on component update seemed preferable to inline ref callbacks and preferable to tracking an array/object of refs
        const activeMenuItemElement = document.getElementById(ACTIVE_MENU_ITEM_ELEMENT_ID); // using plain vanilla because it was not clear how to accomplish changing refs
        if (activeMenuItemElement) {
            this.updateMainModuleGroupNavigationAlignmentValues(activeMenuItemElement);
        }
        const activeMenuItemId = this.getActiveMenuItemId();
        if (this.props.mainNavigationActiveMenuItemId && (this.props.mainNavigationActiveMenuItemId !== activeMenuItemId)) {
            // checked if this.props.mainNavigationActiveMenuItemId is defined to avoid conflict with componentDidMount and DASHBOARD
            this.props.setMainNavigationActiveMenuItemId(this.getActiveMenuItemId());
        }
        this.setState({
            legacyRenderWithoutNavigationModeDetected: activeMenuItemId === undefined ? true : false // set to true when active menu item id cannot be determined
        });// Assumption: any redundant state updates should be ignored
    }

    toggleMainNavigation = () => {
        this.props.setMainNavigation(!this.props.mainNavigationIsCollapsed);
    }

    getActiveMenuItemId = () => {

        let pathname = window.location.pathname; // e.g. "/en/dashboard"
        if (pathname.charAt(0) === "/") {
            pathname = pathname.substring(1); // e.g. "en/dashboard"
        }
        pathname = pathname.toLowerCase();

        let menuItemId = undefined;
        if (pathname.includes("/dashboard")) {
            switch (this.props.mainModuleGroupSelectedResourceType) {
                case RESOURCE_GROUP_TYPES.GEOFENCE:
                    menuItemId = ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE;
                    break;
                case RESOURCE_GROUP_TYPES.ASSET:
                    menuItemId = ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET;
                    break;
                case RESOURCE_GROUP_TYPES.VEHICLE:
                default:
                    menuItemId = ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE;
                    break;
            }
            return menuItemId;
        }

        if (pathname.includes("/stats/history")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW;
            return menuItemId;
        }

        if (pathname.includes("/stats/review/events")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW;
            return menuItemId;
        }

        if (pathname.includes("/stats/review/score")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/trip")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_TRIP;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/activity")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_ACTIVITY;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/event")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_EVENT;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/geofence")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_GEOFENCE;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/raven")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_RAVEN;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/dtc")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_DTC;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/stops")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_STOPS;
            return menuItemId;
        }

        if (pathname.includes("/stats/reports/asset")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.REPORTS_ASSET;
            return menuItemId;
        }

        if (pathname.includes("/settings")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.SETTINGS;
            return menuItemId;
        }

        if (pathname.includes("/account/ravens")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.ACCOUNT_RAVENS;
            return menuItemId;
        }

        if (pathname.includes("/account/tags")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.ACCOUNT_TAGS;
            return menuItemId;
        }

        if (pathname.includes("/account/beacons")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.ACCOUNT_BEACONS;
            return menuItemId;
        }

        if (pathname.includes("/account/users")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.ACCOUNT_USERS;
            return menuItemId;
        }

        if (pathname.includes("/account/profile")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE;
            return menuItemId;
        }

        if (pathname.includes("/snapshot")) {
            menuItemId = ACTIVE_MENU_ITEM_IDS.GRID;
            return menuItemId;
        }

        return menuItemId;
    }

    shouldRenderWithNavigation = () => {
        return this.props.user && this.props.user.email && this.props.ravens && !window.location.pathname.includes("/select-account") ? true : false;
    }

    render() {

        const renderWithNavigation = this.shouldRenderWithNavigation();

        let logoURL = null;
        const filenamePrefix = this.props.features.logo_url;
        const channelBrandingBaseUrl = this.props.features.channel_branding_base_url;
        if (this.props.mainNavigationIsCollapsed) {
            logoURL = '/apple-touch-icon.png'; // small format logo with decent resolution for scale-down
        } else {
            if (filenamePrefix) {
                const base = channelBrandingBaseUrl ? `${channelBrandingBaseUrl}/images` : '/images';
                logoURL = `${base}/${filenamePrefix}-white-${getCurrentLocale()}.png`;
            }    
        }

        const currentLocale = getCurrentLocale();

        let mainNavigationClass = "layout-main-navigation language-subtag-" + currentLocale;

        if (this.props.className) {
            mainNavigationClass += ` ${this.props.className}`;
        }

        if (renderWithNavigation === false) {
            mainNavigationClass += " collapsed";
        } else if (this.props.mainNavigationIsCollapsed) {
            mainNavigationClass += " collapsed";
        }
        if (this.props.fullscreenModeIsActive) {
            mainNavigationClass += " fullscreen-mode-is-active";
        }

        let historyParentItemClass = "history-menu ";
        if (this.props.mainNavigationActiveMenuItemId && this.props.mainNavigationActiveMenuItemId.startsWith("HISTORY")) {
            if (this.state.mainNavigationHistoryIsCollapsed) {
                historyParentItemClass += "layout-main-navigation-active-item-parent-for-main-module-group-navigation";
            }
        }

        let reportsParentItemClass = null;
        if (this.props.mainNavigationActiveMenuItemId && this.props.mainNavigationActiveMenuItemId.startsWith("REPORTS")) {
            if (this.state.mainNavigationReportsIsCollapsed) {
                reportsParentItemClass = "layout-main-navigation-active-item-parent-for-main-module-group-navigation";
            }
        }

        if (this.state.legacyRenderWithoutNavigationModeDetected) {
            mainNavigationClass += " legacy-render-without-navigation-mode";
        }

        return (
            <nav
                ref={this.navigationElm}
                className={mainNavigationClass}
                onMouseEnter={this.navigationMouseEnterHandler}
                onMouseLeave={this.navigationMouseLeaveHandler}
                onScroll={this.navigationScrollHandler}
            >
                <div className="layout-main-navigation-header">
                    <div>
                        <button className="layout-main-navigation-header-menu-icon" onClick={(event) => {
                            if (event.preventDefault) {
                                event.preventDefault();
                            }
                            if (renderWithNavigation) {
                                this.toggleMainNavigation();
                            } else {
                                window.location.href = '/' + getCurrentLocale();
                            }

                        }}>
                            {renderWithNavigation ?
                                <i className="material-icons">menu</i>
                                :
                                null
                            }
                        </button>
                        <div className="layout-main-navigation-header-logo">
                            {logoURL ?
                                <>
                                    {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE ||
                                        this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE ||
                                        this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET ?
                                        <span>
                                            <img src={logoURL} alt="raven logo white" />
                                        </span>
                                        :
                                        <Link to={'/' + currentLocale + '/dashboard'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE); }}>
                                            <img src={logoURL} alt="raven logo white" />
                                        </Link>
                                    }
                                </>
                                :
                                null
                            }
                        </div>
                    </div>
                </div>
                {renderWithNavigation ?
                    <>
                        <ul>
                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE ?
                                    <span>
                                        {strings.resource_group_title_vehicles}
                                    </span>
                                    :
                                    <Link to={'/' + currentLocale + '/dashboard'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.DASHBOARD_VEHICLE); }}>
                                        {strings.resource_group_title_vehicles}
                                    </Link>
                                }
                            </li>
                        </ul>

                        {this.props.user?.gridViewUiEnabled &&
                            <ul>
                                <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.GRID ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                    {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.GRID ?
                                        <span>
                                            {strings.grid_view_title}
                                        </span>
                                        :
                                        <Link to={'/' + currentLocale + '/snapshot'} onClick={
                                            () => {
                                                this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.GRID);
                                                this.props.onSelectEvent(null); //if user has an event clicked in dashboard page, clear it before switching tabs
                                            }
                                        }>
                                            {strings.grid_view_title}
                                        </Link>
                                    }
                                </li>
                            </ul>
                        }

                        <Protected resource='assets' action='read' gate={this.props.features?.has_beacons} showNotFound={false}>
                            <ul>
                                <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                    {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET ?
                                        <span>
                                            {strings.resource_group_title_assets}
                                        </span>
                                        :
                                        <Link to={'/' + currentLocale + '/dashboard'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.DASHBOARD_ASSET); }}>
                                            {strings.resource_group_title_assets}
                                        </Link>
                                    }
                                </li>
                            </ul>
                        </Protected>

                        <Protected resource='geofences' action='read' showNotFound={false}>
                            <ul>
                                <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                    {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE ?
                                        <span>
                                            {strings.geofences_geofences_title}
                                        </span>
                                        :
                                        <Link to={'/' + currentLocale + '/dashboard'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.DASHBOARD_GEOFENCE); }}>
                                            {strings.geofences_geofences_title}
                                        </Link>
                                    }
                                </li>
                            </ul>
                        </Protected>
                        <ul>
                            <Protected resource='coaching' action='read' gate={this.props.features?.fleet_reports} showNotFound={false}>
                                <li className={historyParentItemClass} >
                                    <button onClick={() => { this.setState({ mainNavigationHistoryIsCollapsed: !this.state.mainNavigationHistoryIsCollapsed }) }}>
                                        <span>
                                            {strings.history}
                                        </span>
                                        <i className={this.state.mainNavigationHistoryIsCollapsed ? "material-icons collapsed" : "material-icons"}>keyboard_arrow_up</i>
                                    </button>

                                    <ul className={this.state.mainNavigationHistoryIsCollapsed ? "collapsed" : null} >
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW ?
                                                <span>
                                                    {strings.history_trip_review_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/history'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW); }}>
                                                    {strings.history_trip_review_submenu_item_name}
                                                </Link>
                                            }
                                        </li>

                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW ?
                                                <span>
                                                    {strings.history_event_review_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/review/events'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.HISTORY_EVENT_REVIEW); }}>
                                                    {strings.history_event_review_submenu_item_name}
                                                </Link>
                                            }
                                        </li>

                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE ?
                                                <span>
                                                    {strings.history_safety_score_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/review/score'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.HISTORY_SAFETY_SCORE); }}>
                                                    {strings.history_safety_score_submenu_item_name}
                                                </Link>
                                            }
                                        </li>

                                    </ul>
                                </li>
                                <Protected.Fallback>
                                    <ul>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW ?
                                                <span>
                                                    {strings.history}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/history'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.HISTORY_TRIP_REVIEW); }}>
                                                    {strings.history}
                                                </Link>
                                            }
                                        </li>
                                    </ul>
                                </Protected.Fallback>
                            </Protected>

                            {/* Reports Menu */}
                            <Protected resource='reports' action='view' gate={this.props.features?.fleet_reports} showNotFound={false}>
                                <li className={reportsParentItemClass}>
                                    <button onClick={() => { this.setState({ mainNavigationReportsIsCollapsed: !this.state.mainNavigationReportsIsCollapsed }) }}>
                                        <span>
                                            {strings.reports}
                                        </span>
                                        <i className={this.state.mainNavigationReportsIsCollapsed ? "material-icons collapsed" : "material-icons"}>keyboard_arrow_up</i>
                                    </button>
                                    <ul className={this.state.mainNavigationReportsIsCollapsed ? "collapsed" : null} >
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_TRIP ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_TRIP ?
                                                <span>
                                                    {strings.reports_trip_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/trip'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_TRIP); }}>
                                                    {strings.reports_trip_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_STOPS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_STOPS ?
                                                <span>
                                                    {strings.reports_stops_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/stops'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_STOPS); }}>
                                                    {strings.reports_stops_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_ACTIVITY ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_ACTIVITY ?
                                                <span>
                                                    {strings.reports_activity_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/activity'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_ACTIVITY); }}>
                                                    {strings.reports_activity_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_EVENT ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_EVENT ?
                                                <span>
                                                    {strings.reports_event_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/event'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_EVENT); }}>
                                                    {strings.reports_event_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                        <Protected resource='geofences' action='read' showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_GEOFENCE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_GEOFENCE ?
                                                    <span>
                                                        {strings.reports_geofence_report_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/stats/reports/geofence'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_GEOFENCE); }}>
                                                        {strings.reports_geofence_report_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_RAVEN ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_RAVEN ?
                                                <span>
                                                    {strings.reports_raven_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/raven'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_RAVEN); }}>
                                                    {strings.reports_raven_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>

                                        {/* Assets Report */}
                                        <Protected resource='assets' action='read' gate={this.props.features?.has_beacons} showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_ASSET ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_ASSET ?
                                                    <span>
                                                        {strings.reports_asset_report_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/stats/reports/asset'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_ASSET); }}>
                                                        {strings.reports_asset_report_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_DTC ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.REPORTS_DTC ?
                                                <span>
                                                    {strings.reports_dtc_report_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/stats/reports/dtc'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.REPORTS_DTC); }}>
                                                    {strings.reports_dtc_report_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                    </ul>
                                </li>
                            </Protected>
                        </ul>

                        <ul>
                            <Protected resource='settings' action='read' showNotFound={false}>
                                <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.SETTINGS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                    {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.SETTINGS ?
                                        <span>
                                            {strings.settings}
                                        </span>
                                        :
                                        <Link to={'/' + currentLocale + '/settings'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.SETTINGS); }}>
                                            {strings.settings}
                                        </Link>
                                    }
                                </li>
                            </Protected>

                            {/* Account/Admin */}
                            <Protected resource='menu' action='admin' showNotFound={false}>
                                <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>

                                    <button onClick={() => { this.setState({ mainNavigationAccountIsCollapsed: !this.state.mainNavigationAccountIsCollapsed }) }}>
                                        <span>
                                            {strings.manage_account}
                                        </span>
                                        <i className={this.state.mainNavigationAccountIsCollapsed ? "material-icons collapsed" : "material-icons"}>keyboard_arrow_up</i>
                                    </button>

                                    <ul className={this.state.mainNavigationAccountIsCollapsed ? "collapsed" : null} >

                                        <Protected resource='ravens' action='read' showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_RAVENS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_RAVENS ?
                                                    <span>
                                                        {strings.manage_account_ravens_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/account/ravens'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_RAVENS); }}>
                                                        {strings.manage_account_ravens_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>

                                        <Protected resource='users' action='read' showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_USERS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_USERS ?
                                                    <span>
                                                        {strings.manage_account_users_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/account/users'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_USERS); }}>
                                                        {strings.manage_account_users_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>

                                        <Protected resource='tags' action='read' showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_TAGS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_TAGS ?
                                                    <span>
                                                        {strings.manage_account_tags_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/account/tags'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_TAGS); }}>
                                                        {strings.manage_account_tags_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>

                                        <Protected resource='assets' action='read' gate={this.props.features?.has_beacons} showNotFound={false}>
                                            <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_BEACONS ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                                {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_BEACONS ?
                                                    <span>
                                                        {strings.manage_account_beacons_submenu_item_name}
                                                    </span>
                                                    :
                                                    <Link to={'/' + currentLocale + '/account/beacons'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_BEACONS); }}>
                                                        {strings.manage_account_beacons_submenu_item_name}
                                                    </Link>
                                                }
                                            </li>
                                        </Protected>


                                        {/* account misc (theme, ...)  */}
                                        <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                            {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE ?
                                                <span>
                                                    {strings.manage_account_profile_submenu_item_name}
                                                </span>
                                                :
                                                <Link to={'/' + currentLocale + '/account/profile'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE); }}>
                                                    {strings.manage_account_profile_submenu_item_name}
                                                </Link>
                                            }
                                        </li>
                                    </ul>
                                </li>

                                <Protected.Fallback>
                                    <li id={this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE ? ACTIVE_MENU_ITEM_ELEMENT_ID : null}>
                                        {this.props.mainNavigationActiveMenuItemId === ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE ?
                                            <span>
                                                {strings.manage_account_profile_submenu_item_name}
                                            </span>
                                            :
                                            <Link to={'/' + currentLocale + '/account/profile'} onClick={() => { this.props.setMainNavigationActiveMenuItemId(ACTIVE_MENU_ITEM_IDS.ACCOUNT_PROFILE); }}>
                                                {strings.manage_account_profile_submenu_item_name}
                                            </Link>
                                        }
                                    </li>
                                </Protected.Fallback>
                            </Protected>

                        </ul>
                    </>
                    :
                    null
                }
                <div className="simulatedInnerShadow"></div>
                <div className='layout-main-navigation-bottom-shadow'><i className="material-icons">keyboard_double_arrow_down</i></div>
            </nav>
        )
    }
}

const mapStateToProps = (state, ownProps) => {
    if (!state.data.layoutTemplateModularFullscreenSharedState) {
        console.error("LayoutTemplateModularFullscreen mapStateToProps layoutTemplateModularFullscreenSharedState reducer property not found");
        return {};
    }

    const { layoutTemplateModularFullscreenSharedState } = state.data;

    return {
        tags: state.data.tags.list,
        ...layoutTemplateModularFullscreenSharedState // shared between modular fullscreen group and the main and user menus
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    return {
        setMainNavigation: (isCollapsed) => {
            dispatch(dispatchActions.setMainNavigation(isCollapsed));
            return;
        },
        setMainNavigationActiveMenuItemId: (activeMenuItemId) => {
            dispatch(dispatchActions.setMainNavigationActiveMenuItemId(activeMenuItemId));
            return;
        },
        setMainModuleGroupSelectedResourceType: (resourceType) => {
            dispatch(dispatchActions.setMainModuleGroupSelectedResourceType(resourceType));
            return;
        },
        setMainModuleGroupNavigation: (isCollapsed) => {
            dispatch(dispatchActions.setMainModuleGroupNavigation(isCollapsed));
            return;
        },
        setMainNavigationActiveMenuItemDomElementTop: (domElementTop) => {
            dispatch(dispatchActions.setMainNavigationActiveMenuItemDomElementTop(domElementTop));
            return;
        }
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MainNavigation);
